import { Link } from "react-router-dom";
import { GraveIcon } from "./GraveIcon";
import { SeverityDots } from "./SeverityMark";
import NewMark from "./NewMark";
import { Drop, Plant as PlantIcon, Star, Flame, CheckCircle, Lamp } from "@phosphor-icons/react";
import { fileUrl, focusStyle } from "../lib/api";
import SmoothImg from "./SmoothImg";
import { REMINDER_META, STATUS_COLOR, reminderText } from "../lib/reminders";
import { daysAgo, sevMeta, PLANT_STATUS, formatDate } from "../lib/theme";

export function StreakBadge({ streak, onSchedule, id }) {
  if (streak >= 3) return (
    <span data-testid={`plant-card-streak-${id}`} title={`${streak} waterings in a row on time`}
      className="shrink-0 inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full text-[11px] font-semibold text-[#FF7A00] bg-[#FF9F0A]/[0.12]">
      <Flame weight="fill" className="h-3.5 w-3.5" />{streak} on time
    </span>
  );
  if (onSchedule) return (
    <span data-testid={`plant-card-onschedule-${id}`} title="Nothing overdue"
      className="shrink-0 inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full text-[11px] font-medium text-[#248A3D] dark:text-[#30D158] bg-[#30D158]/[0.12]">
      <CheckCircle weight="fill" className="h-3.5 w-3.5" />On schedule
    </span>
  );
  return null;
}

export default function PlantCard({ plant, reminder, lastWatered, favorite, onToggleFavorite, streak = 0, priority = false }) {
  const img = fileUrl(plant.photo_path, 480);
  const lastWateredDays = daysAgo(lastWatered);
  const critical = plant.critical_active;
  const sev = sevMeta(plant.critical_severity);
  const dead = plant.status === "deceased";
  const status = PLANT_STATUS[plant.status] && plant.status !== "active" && !dead ? PLANT_STATUS[plant.status] : null;
  return (
    <Link
      to={`/plants/${plant.id}`}
      data-testid={`plant-card-${plant.id}`}
      data-deceased={dead}
      data-acute={critical ? "true" : undefined}
      className={`plant-card group block rounded-3xl bg-card overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-16px_rgba(0,0,0,0.14)] ${dead ? "[&_.plant-card-meta]:opacity-60" : ""}`}
    >
      <div className="aspect-[4/3] bg-muted relative overflow-hidden rounded-t-3xl isolate [transform:translateZ(0)]">
        {dead && (
          <span data-testid={`plant-card-deceased-${plant.id}`} title="Deceased" aria-label="Deceased"
            className="absolute top-3 left-3 z-10 h-8 w-8 rounded-full bg-background/90 fine:bg-background/80 fine:backdrop-blur-md shadow-[0_2px_8px_-2px_rgba(0,0,0,0.25)] flex items-center justify-center">
            <GraveIcon weight="fill" className="h-4 w-4 text-[#636366]" />
          </span>
        )}
        {critical && (
          <span data-testid={`plant-card-critical-${plant.id}`} data-severity={plant.critical_severity || "unknown"} title={`${sev.label} acute event open`}
            className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 h-7 pl-2.5 pr-3 rounded-full bg-background/90 fine:bg-background/80 fine:backdrop-blur-md shadow-[0_2px_8px_-2px_rgba(0,0,0,0.25)] text-[11px] font-semibold text-foreground">
            <SeverityDots level={sev.level} color={sev.color} size="sm" />
            {sev.label}
          </span>
        )}
        {img ? (
          <div className={`w-full h-full group-hover:scale-[1.03] transition-transform duration-500 ${dead ? "grayscale opacity-55" : ""}`}>
            <SmoothImg src={img} loading={priority ? "eager" : "lazy"} fetchpriority={priority ? "high" : "auto"} alt={plant.nickname} className="w-full h-full object-cover" style={focusStyle(plant.photo_focus)} />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <PlantIcon weight="duotone" className="h-10 w-10 opacity-40" />
          </div>
        )}
        <button
          type="button"
          data-testid={`favorite-toggle-${plant.id}`}
          aria-label={favorite ? "Remove from favorites" : "Add to favorites"}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite?.(plant.id); }}
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-background/90 fine:bg-background/85 fine:backdrop-blur flex items-center justify-center hover:scale-110 transition-transform"
        >
          <Star weight={favorite ? "fill" : "regular"} className={`h-4 w-4 ${favorite ? "text-[#FF9F0A]" : "text-muted-foreground"}`} />
        </button>
        {status && (
          <span className="absolute bottom-3 left-3 text-[10px] font-medium px-2 py-1 rounded-full bg-background/90 fine:bg-background/85 fine:backdrop-blur" style={{ color: status.color }}>{status.label}</span>
        )}
      </div>
      <div className="p-4 sm:p-5">
        <div className="min-w-0">
            <h3 className={`font-semibold text-base tracking-tight truncate flex items-center gap-1.5 ${dead ? "text-foreground/60" : ""}`}>
              <span className="truncate">{plant.nickname}</span>
              <NewMark plant={plant} testid={`plant-card-new-${plant.id}`} />
              {plant.grow_light && !dead && <Lamp weight="fill" className="h-3.5 w-3.5 shrink-0 text-[#E8A100]" aria-label="Under grow light" data-testid={`plant-card-growlight-${plant.id}`} />}
            </h3>
            {dead ? (
              <p className="text-xs text-muted-foreground truncate flex items-center gap-1" data-testid={`plant-card-deceased-label-${plant.id}`}>
                <GraveIcon weight="fill" className="h-3 w-3 shrink-0" />{plant.deceased_cause || "Deceased"}{plant.deceased_at ? ` · ${formatDate(plant.deceased_at)}` : ""}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground truncate">{[plant.location, plant.scientific_name || plant.species].filter(Boolean).join(" · ") || "Unidentified"}</p>
            )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 text-xs min-w-0">
          {dead ? <span className="text-muted-foreground italic truncate min-w-0" data-testid={`plant-card-memorial-${plant.id}`}>{plant.deceased_note ? `“${plant.deceased_note}”` : "Record kept for reference"}</span> : reminder ? (
            <span data-testid={`plant-card-reminder-${plant.id}`} className="inline-flex items-center gap-1.5 font-medium truncate" style={{ color: STATUS_COLOR[reminder.status] }}>
              {(() => { const I = REMINDER_META[reminder.event_type].Icon; return <I weight="fill" className="h-3.5 w-3.5 shrink-0" />; })()}
              {reminderText(reminder)}
            </span>
          ) : <span className="text-muted-foreground inline-flex items-center gap-1.5">
            <Drop weight="regular" className="h-3.5 w-3.5" />
            {lastWateredDays === null ? "Never watered" : lastWateredDays === 0 ? "Watered today" : `Watered ${lastWateredDays}d ago`}
          </span>}
          {!dead && streak >= 3 && <span data-testid={`plant-card-streak-${plant.id}`} title={`${streak} waterings in a row on time`} className="shrink-0 inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#FF7A00]"><Flame weight="fill" className="h-3.5 w-3.5" />{streak}</span>}
        </div>
      </div>
    </Link>
  );
}
